/**
 * WebAuthn (Touch ID / Face ID) plumbing for the auth screens — the browser half of the four
 * `/api/v1/auth/passkey/*` routes the backend already ships.
 *
 * WHY THIS EXISTS AS A MODULE: the WebAuthn API speaks `ArrayBuffer`, the wire speaks base64url, and
 * getting that boundary wrong fails as an opaque `NotAllowedError` rather than a useful message. Keeping
 * every conversion here means the screens deal only in "start" and "finish".
 *
 * SECURITY NOTES, because this is the authentication surface:
 *  - Nothing is persisted. No token, no credential id, no "has a passkey" hint reaches `localStorage`
 *    or `sessionStorage`; the session that comes back rides `TokenProvider` exactly like a password login.
 *  - We never ask the server "does this address have a passkey" — the legacy app's `GET /webauthn/check
 *    ?email=` was an account-enumeration oracle and is deliberately NOT ported. `passkey/auth/options`
 *    answers a well-formed challenge for an unknown address too, so the UI cannot tell the difference.
 *
 * WIRE SHAPE — read the CONTROLLER, not the service record. `POST /auth/passkey/{auth,register}/options`
 * answer `{ challengeId, publicKey: { ... } }`. The internal record is
 * `PasskeyAuthOptions(Guid ChallengeId, string OptionsJson)` and looks exactly like the DTO, but
 * `AuthController.PasskeyOptions` re-wraps it and parses the JSON on the way out. Both endpoints declare
 * `[ProducesResponseType(200)]` with NO type argument, so the OpenAPI document carries no schema for them
 * and the generated client cannot type-check the read — the first version of this module cast
 * `begin.data as { challengeId, optionsJson }`, which type-checked against nothing, made `optionsJson`
 * `undefined`, and killed every passkey call in `JSON.parse(undefined)` (TASK-062, reported by the SME
 * after !11 shipped). Typing those two endpoints is the permanent fix and is logged separately.
 *  - A user cancelling the platform prompt is NOT an error — it is the most common outcome (they changed
 *    their mind, or the finger did not read). It must never render as "sign-in failed".
 *
 * WHAT THE SERVER REQUIRES BACK, and why the return types below are pinned to the generated schema.
 * The two verify routes bind onto Fido2NetLib's `Authenticator{Attestation,Assertion}RawResponse`, whose
 * non-nullable members ASP.NET treats as REQUIRED — so `clientExtensionResults` (both ceremonies) and
 * `response.transports` (enrolment) are not optional extras: omitting either is a **400 before any
 * verification runs**, which reads at the UI exactly like a rejected passkey. That is what shipped, and it
 * killed every sign-in and every enrolment on every browser from !18 until 2026-09-06 — the SAME failure
 * as the `optionsJson` one above, one route later: the OpenAPI schema declared both fields required and
 * the generated client would have refused to compile, but `session.ts` cast each body `as never`. The
 * casts are gone and these functions now return the generated types, so the contract is a compile error
 * rather than a runtime 400. `getClientExtensionResults()` and `getTransports()` are the browser's own
 * accessor for the transports; the extension outputs are `{}` because we request no extensions.
 */

import { isNative } from "@cntrl/native";
import type { components } from "@cntrl/api-client";

/** Exactly what `POST /auth/passkey/auth` binds `credential` onto — pinned, not hand-shaped. */
export type PasskeyAssertion = components["schemas"]["AuthenticatorAssertionRawResponse"];
/** Exactly what `POST /auth/passkey/register` binds `credential` onto — pinned, not hand-shaped. */
export type PasskeyAttestation = components["schemas"]["AuthenticatorAttestationRawResponse"];
type Transport = components["schemas"]["AuthenticatorTransport"];

/** The closed transport vocabulary the server's enum accepts. A browser may report a value outside it
 *  (the spec is open-ended and engines add to it), and an unknown member fails the enum bind — 400 — so
 *  anything unrecognised is DROPPED rather than forwarded. Transports are a routing hint for a later
 *  `allowCredentials`, never part of the signature, so dropping one costs nothing; forwarding one costs
 *  the whole enrolment. */
const KNOWN_TRANSPORTS: readonly string[] = ["usb", "nfc", "ble", "smart-card", "hybrid", "internal"];

/**
 * The extension-outputs member both ceremonies must send.
 *
 * `{}` is the honest value, not a stub: neither options builder requests a single extension, so the
 * browser has nothing to report and an empty object is precisely what happened. What it is NOT is the
 * same as omitting the member — that is the 400 this whole note exists for.
 *
 * We deliberately do NOT forward `credential.getClientExtensionResults()` verbatim. Its values are
 * live browser objects: `prf.results.first` is a `BufferSource`, which JSON-serialises to `{}` and would
 * fail the server's `byte[]` bind. The compiler refuses that assignment, and it is right to — if an
 * extension is ever requested, this needs a real base64url serialiser per extension, not a cast.
 */
function extensionOutputs(): PasskeyAssertion["clientExtensionResults"] {
  return {};
}

/**
 * The platform authenticator is present AND usable (Touch ID / Face ID / Windows Hello).
 *
 * FALSE IN THE NATIVE WRAPPER, unconditionally, and that is not a capability probe failing — it is one
 * succeeding about a ceremony that cannot complete. The Capacitor webview serves the bundle from
 * `https://localhost` (`capacitor.config.ts`, a deliberately frozen hostname), while the server's `rpId` is
 * the lane's real host. WebAuthn requires the rpId to be a registrable-domain suffix of the PAGE origin, and
 * `dev.artistcntrl.app` is not a suffix of `localhost` — so the browser rejects the call before the reader
 * is consulted, in both directions (the server's accepted origin would not match `https://localhost`
 * either). iOS additionally needs a `webcredentials:` associated domain, which `localhost` can never have.
 *
 * `isUserVerifyingPlatformAuthenticatorAvailable()` answers TRUE there regardless — the hardware is
 * genuinely present — so without this the wrapper renders a Touch ID button that can only ever fail. A
 * control whose one outcome is an error is the "dead button" this codebase already refuses elsewhere.
 * Resolving it means giving the webview a real origin (see the wrapper's README); until then the affordance
 * belongs only where it can work.
 */
export async function passkeySupported(): Promise<boolean> {
  if (typeof window === "undefined" || !window.PublicKeyCredential) return false;
  if (isNative()) return false;
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;   // an older engine that has the constructor but not the probe
  }
}

/** True when the platform prompt was dismissed rather than failing — the caller should stay silent. */
export function isPasskeyCancel(err: unknown): boolean {
  return err instanceof DOMException && (err.name === "NotAllowedError" || err.name === "AbortError");
}

/**
 * True when ENROLMENT was refused because this authenticator already holds a credential for the account.
 *
 * `InvalidStateError` is WebAuthn's way of saying *"you are already set up here"*, raised by the platform
 * against the `excludeCredentials` list `BeginRegistrationAsync` populates from what the account already
 * holds. It is the one DOMException from `create()` that means the caller's GOAL IS ALREADY MET — so it is
 * neither a cancel (they did not decline) nor a failure (nothing is wrong), and reporting it as either is
 * a lie in a different direction. It reaches the client with NO server round trip: the browser refuses
 * before the POST, so nothing is logged anywhere and the only evidence is this exception's name.
 */
export function isPasskeyAlreadyEnrolled(err: unknown): boolean {
  return err instanceof DOMException && err.name === "InvalidStateError";
}

// ---- base64url ⇄ ArrayBuffer ---------------------------------------------------------------------
// WebAuthn JSON is base64URL (RFC 4648 §5): `-`/`_` for `+`/`/`, padding stripped.

function fromB64Url(value: string): ArrayBuffer {
  const b64 = value.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

function toB64Url(buf: ArrayBuffer | null): string {
  if (!buf) return "";
  let bin = "";
  for (const b of new Uint8Array(buf)) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
  * The server hands us the options as an OBJECT with base64url fields; WebAuthn wants buffers.
  *
  * NOT a JSON string. `AuthController.PasskeyOptions` builds `{ challengeId, publicKey: <parsed> }` — it
  * re-wraps the service's `PasskeyAuthOptions(ChallengeId, OptionsJson)` record and parses the JSON on the
  * way out, so `optionsJson` never reaches the wire. The first version of this file read that field anyway
  * (see the header note) and every passkey call died in `JSON.parse(undefined)`.
  */
function decodeRequestOptions(publicKey: unknown): PublicKeyCredentialRequestOptions {
  const o = publicKey as Record<string, unknown>;
  const allow = (o.allowCredentials ?? []) as { id: string; type?: string; transports?: string[] }[];
  return {
    ...(o as object),
    challenge: fromB64Url(String(o.challenge)),
    allowCredentials: allow.map((c) => ({
      id: fromB64Url(c.id),
      type: "public-key" as const,
      ...(c.transports ? { transports: c.transports as AuthenticatorTransport[] } : {}),
    })),
  } as PublicKeyCredentialRequestOptions;
}

function decodeCreationOptions(publicKey: unknown): PublicKeyCredentialCreationOptions {
  const o = publicKey as Record<string, unknown>;
  const user = o.user as { id: string; name: string; displayName: string };
  const exclude = (o.excludeCredentials ?? []) as { id: string }[];
  return {
    ...(o as object),
    challenge: fromB64Url(String(o.challenge)),
    user: { ...user, id: fromB64Url(user.id) },
    excludeCredentials: exclude.map((c) => ({ id: fromB64Url(c.id), type: "public-key" as const })),
  } as PublicKeyCredentialCreationOptions;
}

/** Drive the platform prompt for SIGN-IN, and serialise the assertion for `passkey/auth`. */
export async function passkeyGet(publicKey: unknown): Promise<PasskeyAssertion> {
  const credential = (await navigator.credentials.get({
    publicKey: decodeRequestOptions(publicKey),
  })) as PublicKeyCredential | null;
  if (!credential) throw new DOMException("No credential returned", "NotAllowedError");

  const r = credential.response as AuthenticatorAssertionResponse;
  return {
    id: credential.id,
    rawId: toB64Url(credential.rawId),
    type: "public-key",
    // REQUIRED by the server's bind — see the header note.
    clientExtensionResults: extensionOutputs(),
    response: {
      clientDataJSON: toB64Url(r.clientDataJSON),
      authenticatorData: toB64Url(r.authenticatorData),
      signature: toB64Url(r.signature),
      userHandle: r.userHandle ? toB64Url(r.userHandle) : null,
    },
  };
}

/** Drive the platform prompt for ENROLMENT, and serialise the attestation for `passkey/register`. */
export async function passkeyCreate(publicKey: unknown): Promise<PasskeyAttestation> {
  const credential = (await navigator.credentials.create({
    publicKey: decodeCreationOptions(publicKey),
  })) as PublicKeyCredential | null;
  if (!credential) throw new DOMException("No credential returned", "NotAllowedError");

  const r = credential.response as AuthenticatorAttestationResponse;
  return {
    id: credential.id,
    rawId: toB64Url(credential.rawId),
    type: "public-key",
    // Both REQUIRED by the server's bind — see the header note.
    clientExtensionResults: extensionOutputs(),
    response: {
      clientDataJSON: toB64Url(r.clientDataJSON),
      attestationObject: toB64Url(r.attestationObject),
      transports: readTransports(r),
    },
  };
}

/** `getTransports()` is absent on older engines (it post-dates the ceremony itself), and its result is
 *  open-ended. Missing accessor ⇒ `[]`, which the server accepts; an unknown member ⇒ dropped. */
function readTransports(r: AuthenticatorAttestationResponse): Transport[] {
  const get = (r as { getTransports?: () => string[] }).getTransports;
  if (typeof get !== "function") return [];
  try {
    return get.call(r).filter((t): t is Transport => KNOWN_TRANSPORTS.includes(t));
  } catch {
    return [];
  }
}
